import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  CreditCard,
  AlertTriangle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { color: "text-yellow-500 bg-yellow-500/20", icon: Clock, label: "Pending" },
  processing: { color: "text-blue-500 bg-blue-500/20", icon: Loader2, label: "Processing" },
  completed: { color: "text-green-500 bg-green-500/20", icon: CheckCircle, label: "Completed" },
  failed: { color: "text-red-500 bg-red-500/20", icon: XCircle, label: "Failed" }
};

export default function AdminPayouts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    // Set admin token from localStorage (using correct key: admin_token)
    const adminToken = localStorage.getItem("admin_token");
    if (!adminToken) {
      // Redirect to admin login if no token
      window.location.href = "/admin";
      return;
    }
    axios.defaults.headers.common["Authorization"] = `Bearer ${adminToken}`;
    fetchPayouts();
  }, []);

  const fetchPayouts = async () => {
    try {
      const response = await axios.get(`${API}/admin/affiliate-payouts`);
      setData(response.data);
    } catch (error) {
      toast.error("Failed to load payouts");
    } finally {
      setLoading(false);
    }
  };

  const processPayout = async (payoutId, newStatus) => {
    setProcessing(payoutId);
    try {
      await axios.put(`${API}/admin/affiliate-payout/${payoutId}/process?new_status=${newStatus}`);
      toast.success(`Payout marked as ${newStatus}`);
      fetchPayouts();
    } catch (error) {
      toast.error("Failed to process payout");
    } finally {
      setProcessing(null);
    }
  };

  const filteredPayouts = data?.payouts?.filter(p => 
    filter === "all" ? true : p.status === filter
  ) || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <DollarSign className="h-12 w-12 text-green-500 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-primary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/admin/affiliates" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Affiliates
            </Link>
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-500" />
              <span className="font-bold tracking-tight">PAYOUT MANAGEMENT</span>
            </div>
            <div className="w-20" />
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card 
              className={`bg-card cursor-pointer transition-all ${filter === 'pending' ? 'border-yellow-500' : 'border-border hover:border-yellow-500/50'}`}
              onClick={() => setFilter('pending')}
            >
              <CardContent className="p-4 text-center">
                <Clock className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
                <p className="text-2xl font-black">{data?.summary?.pending_count || 0}</p>
                <p className="text-xs text-muted-foreground uppercase">Pending</p>
                <p className="text-sm text-yellow-500 font-bold">${(data?.summary?.pending_total || 0).toFixed(2)}</p>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card 
              className={`bg-card cursor-pointer transition-all ${filter === 'processing' ? 'border-blue-500' : 'border-border hover:border-blue-500/50'}`}
              onClick={() => setFilter('processing')}
            >
              <CardContent className="p-4 text-center">
                <Loader2 className="h-6 w-6 text-blue-500 mx-auto mb-2" />
                <p className="text-2xl font-black">{data?.summary?.processing_count || 0}</p>
                <p className="text-xs text-muted-foreground uppercase">Processing</p>
                <p className="text-sm text-blue-500 font-bold">${(data?.summary?.processing_total || 0).toFixed(2)}</p>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card 
              className={`bg-card cursor-pointer transition-all ${filter === 'completed' ? 'border-green-500' : 'border-border hover:border-green-500/50'}`}
              onClick={() => setFilter('completed')}
            >
              <CardContent className="p-4 text-center">
                <CheckCircle className="h-6 w-6 text-green-500 mx-auto mb-2" />
                <p className="text-2xl font-black">{data?.summary?.completed_count || 0}</p>
                <p className="text-xs text-muted-foreground uppercase">Completed</p>
                <p className="text-sm text-green-500 font-bold">${(data?.summary?.completed_total || 0).toFixed(2)}</p>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card 
              className={`bg-card cursor-pointer transition-all ${filter === 'all' ? 'border-primary' : 'border-border hover:border-primary/50'}`}
              onClick={() => setFilter('all')}
            >
              <CardContent className="p-4 text-center">
                <CreditCard className="h-6 w-6 text-primary mx-auto mb-2" />
                <p className="text-2xl font-black">{data?.payouts?.length || 0}</p>
                <p className="text-xs text-muted-foreground uppercase">All Payouts</p>
                <p className="text-sm text-primary font-bold">View All</p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Payouts List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-sm uppercase tracking-wider flex items-center gap-2">
                <DollarSign className="h-4 w-4" /> 
                {filter === 'all' ? 'All Payouts' : `${filter.charAt(0).toUpperCase() + filter.slice(1)} Payouts`}
                <span className="text-muted-foreground">({filteredPayouts.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredPayouts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No {filter === 'all' ? '' : filter} payouts
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredPayouts.map((payout) => {
                    const StatusIcon = STATUS_CONFIG[payout.status]?.icon || Clock;
                    return (
                      <div
                        key={payout.id}
                        className="flex items-center justify-between p-4 rounded-lg bg-secondary/30 border border-border"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-full ${STATUS_CONFIG[payout.status]?.color}`}>
                            <StatusIcon className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="font-bold">{payout.affiliate_name}</p>
                            <p className="text-xs text-muted-foreground">{payout.affiliate_email}</p>
                            <p className="text-xs text-muted-foreground">
                              {payout.payout_method} • {new Date(payout.requested_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-2xl font-black text-green-500">${payout.amount.toFixed(2)}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_CONFIG[payout.status]?.color}`}>
                              {STATUS_CONFIG[payout.status]?.label}
                            </span>
                          </div>
                          
                          {payout.status === 'pending' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => processPayout(payout.id, 'processing')}
                                disabled={processing === payout.id}
                                className="bg-blue-500 hover:bg-blue-600"
                              >
                                {processing === payout.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Process"}
                              </Button>
                            </div>
                          )}
                          
                          {payout.status === 'processing' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => processPayout(payout.id, 'completed')}
                                disabled={processing === payout.id}
                                className="bg-green-500 hover:bg-green-600"
                              >
                                {processing === payout.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Complete"}
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => processPayout(payout.id, 'failed')}
                                disabled={processing === payout.id}
                              >
                                Fail
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
